import React, { useEffect, useState } from 'react'
import { faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Col, Collapse, Row, Select } from 'antd';
import ButtonIcon from 'components/ButtonComp';
import InputComp from 'components/InputComp';
import ModalComp from 'components/ModalComp'
import SelectComp from 'components/SelectComp';
import useForm from 'hooks/useForm';
import useLoading from 'hooks/useLoading';
import usePagination from 'hooks/usePagination';
import useProjects from 'hooks/useProjects';
import useSegments from 'hooks/useSegments';
import { IAssetBaseLine } from 'models/AssetBaseLine';
import { IAsset } from 'models/Assets';
import { IAssetType } from 'models/AssetsTypes';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { itsItem } from 'redux/state/inventoryState';
import useConfirm from 'hooks/useConfirm';
import { useNotificationContext } from 'services/NotificationContext';
import { storeAssets, uploadInventoryInvoiceFiles } from 'api/its/assets';
import { STATUS_OK } from 'libs/fetcher';
import app from '../../../styles/addElement.module.css';

type Props = {
    isOpen?: boolean;
    onClose?: () => void;
    hookAssets: ReturnType<typeof usePagination<IAsset>>;
    assetTypes: IAssetType[];
    assetBaseLines: IAssetBaseLine[];
}

const AddElementModal: React.FC<Props> = ({
    isOpen = false,
    onClose = () => { console.log('Modal closed') },
    hookAssets,
    assetTypes,
    assetBaseLines
}: Props) => {
    const items = useSelector((state: RootState) => state.inventory.its);
    const hookLoading = useLoading({ defaultLoading: false });
    const hookProjects = useProjects({ package_id: 17 });
    const hookForm = useForm<Record<string, any>>({ assets_base_line_id: 'all', type_id: 'all', });
    const { project_id } = hookForm.form; 
    const currentSegments = useSegments({ project_id });
    const [itemsAdd, setItemsAdd] = useState<itsItem[]>([]);
    const [ExtraFields, setExtraFields] = useState<itsItem[]>([]);
    const { showPromiseConfirm } = useConfirm();
    const { show } = useNotificationContext();

    useEffect(() => {
        const filteredItems = items.filter((item: itsItem) => item.add === true && !item.newValues);
        setItemsAdd(filteredItems);
        setExtraFields(items.filter((item: itsItem) => item.newValues))
    }, [items]);

    // Llenar campos del formulario al obtener los segmentos, se toma el primero por default
    useEffect(() => {
        const { data: dataSegments } = currentSegments;
        if (dataSegments.length > 0) {
            hookForm.setForm({
                ...hookForm.form,
                segment_id: dataSegments[0].id,
                body: dataSegments[0].name,
                direction: dataSegments[0].location
            });
        }
    }, [currentSegments.data]);

    // Al cambiar segmentos, se actualizan los datos del formulario
    useEffect(() => {
        if (hookForm.form?.segment_id) {
            const segment = currentSegments.data.find((item) => item.id == hookForm.form?.segment_id);
            if (segment)
                hookForm.setForm({
                    ...hookForm.form,
                    body: segment.name,
                    direction: segment.location
                });
        }
    }, [hookForm.form?.segment_id]);

    // Seleccionar el primer asset_type por default
    useEffect(() => {
        if (assetTypes.length > 0)
            hookForm.setForm({ ...hookForm.form, type_id: assetTypes[0].id });
    }, [assetTypes]);

    // Seleccionar la primera linea base por default
    useEffect(() => {
        if (assetBaseLines.length > 0)
            hookForm.setForm({ ...hookForm.form, assets_base_line_id: assetBaseLines[0].id });
    }, [assetBaseLines]);

    const onSave = async () => {
        hookLoading.start();
        showPromiseConfirm({
            title: '¿Desea realizar esta operación?',
            content: <>
                <p>Usted está a punto de guardar un activo en la base de datos</p>
            </>,
            okText: 'Si, realizar!',
            actionCallback: async () => {
                const { project_id, type_id, assets_base_line_id, ...rest } = hookForm.form;
                const data = { type_id, assets_base_line_id, ...rest };
                const response = await storeAssets({ project_id, data, table: 'assets_its' });
                if (response.status === STATUS_OK && response.data) {
                    hookAssets.addElement(response.data);
                    if (hookForm.file) {
                        const data = new FormData();
                        data.append('invoice_file', hookForm.file);
                        await uploadInventoryInvoiceFiles({
                            data,
                            id: response.data.id,
                            table: 'assets_its',
                            project_id
                        })
                    }
                    show(
                        'success',
                        'Operación exitosa!',
                        'Transacción realizada con éxito'
                    );
                    onClose();
                } else {
                    show(
                        'error',
                        'Operación fallida!',
                        'Ha ocurrido un error al tratar de almacenar el activo, verifique el formulario.'
                    );
                }
            },
        });
        hookLoading.end();
    }

    const handleSelect = (value: string, name: string) => hookForm.setForm({ ...hookForm.form, [name]: value })

    return (
        <ModalComp
            title='Agregar elemento'
            isOpen={isOpen}
            onClose={onClose}
            loading={hookLoading.isLoading}
            width={'30rem'}
            footer={<>
                <ButtonIcon name='Cerrar' icon={faTimes} onClick={onClose} variant='outline-secondary' />
                <ButtonIcon name='Agregar' icon={faSave} onClick={onSave} variant='outline-primary' />
            </>}
        >
            <Row gutter={[16, 16]} style={{ marginBottom: '0.2rem' }}>
                <Col span={12}>
                    <SelectComp
                        title='Proyecto'
                        name='project_id'
                        value={hookForm.form?.project_id}
                        options={[{
                            label: '-- Seleccionar --', value: '0'
                        },
                        ...hookProjects.data.map(item => ({ label: item.name, value: item.id.toString() }))
                        ]}
                        onChange={hookForm.handleChange}
                    />
                </Col>
                <Col span={12}>
                    <SelectComp
                        title='Línea base'
                        name='assets_base_line_id'
                        value={hookForm.form?.assets_base_line_id}
                        options={[{
                            label: '-- Seleccionar --', value: '0'
                        },
                        ...assetBaseLines.map(item => ({ label: item.name, value: item.id.toString() }))
                        ]}
                        onChange={hookForm.handleChange}
                    />
                </Col>
                <Col span={12}>
                    <SelectComp
                        title='Tipo de Activo'
                        name='type_id'
                        value={hookForm.form?.type_id}
                        options={[{
                            label: '-- Seleccionar --', value: '0'
                        },
                        ...assetTypes.map(item => ({ label: item.name, value: item.id.toString() }))
                        ]}
                        onChange={hookForm.handleChange}
                    />
                </Col>
                <Col span={12}>
                    <SelectComp
                        title='Segmento'
                        name='segment_id'
                        value={hookForm.form?.segment_id}
                        options={[{
                            label: '-- Seleccionar --', value: '0'
                        },
                        ...currentSegments.data.map(item => ({ label: item.name, value: item.id.toString() }))
                        ]}
                        onChange={hookForm.handleChange}
                    />
                </Col>

                {/* Atributos generales */}
                {
                    itemsAdd.map((item) =>
                        <Col span={12}>
                            <InputComp title={item.label} name={item.key} value={hookForm.form[item.key]} onChange={hookForm.handleChange} />
                        </Col>
                    )
                }
            </Row>
            <Collapse>
                <Collapse.Panel header="Agregar información extra" key={1}>
                    <Row gutter={[16, 16]} style={{ marginBottom: '0.2rem' }}>
                        {
                            ExtraFields.map((field, index) => {
                                if (field.type === "select" && field?.selectValues?.length) {
                                    return <Col key={index} span={12} className='gutter-row'>
                                        <div className={app.label__extra}>{field.label}</div>
                                        <Select
                                            size='large'
                                            style={{ width: "100%", paddingLeft: 15, paddingRight: 15 }}
                                            options={field?.selectValues}
                                            onChange={value => handleSelect(value, field.key)}
                                        />
                                    </Col>
                                }

                                return <Col span={12}>
                                    <InputComp
                                        type={field.type}
                                        title={field.label}
                                        name={field.key}
                                        value={hookForm.form[field.key]}
                                        onChange={hookForm.handleChange}
                                    />
                                </Col>
                            })
                        }
                    </Row>
                </Collapse.Panel>
            </Collapse>
        </ModalComp>
    )
}

export default AddElementModal